import { apiClient } from '../config';
import type {
  Organization,
  OrganizationMember,
  Site,
  Device,
  DeviceHeartbeat,
  DeviceEvent,
  AlertConfig,
  AlertHistory,
  FleetOverview,
  FleetMapData,
  PlayerHealthDevice,
  PlayerHealthListResponse,
  PlayerEventsResponse,
} from '../types';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  error?: string;
}

interface PaginatedData<T> {
  success: boolean;
  data: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export class FleetService {
  // Fleet Overview
  static async getOverview(): Promise<FleetOverview> {
    const response = await apiClient.get<ApiResponse<FleetOverview>>('/api/fleet/overview');
    if (!response.data.success) throw new Error(response.data.error || 'Failed to fetch overview');
    return response.data.data;
  }

  // Fleet Map Data
  static async getMapData(): Promise<FleetMapData> {
    const response = await apiClient.get<ApiResponse<FleetMapData>>('/api/fleet/map');
    if (!response.data.success) throw new Error(response.data.error || 'Failed to fetch map data');
    return response.data.data;
  }

  // Fleet Alerts
  static async getAlerts(hours: number = 24): Promise<DeviceEvent[]> {
    const response = await apiClient.get<ApiResponse<DeviceEvent[]>>(`/api/fleet/alerts?hours=${hours}`);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to fetch alerts');
    return response.data.data;
  }

  // Organizations
  static async listOrganizations(params?: { page?: number; size?: number }): Promise<PaginatedData<Organization>> {
    const cleanParams: Record<string, string> = {};
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value != null && String(value) !== '') {
          cleanParams[key] = String(value);
        }
      });
    }
    const queryString = new URLSearchParams(cleanParams).toString();
    const response = await apiClient.get<PaginatedData<Organization>>(`/api/organizations?${queryString}`);
    return response.data;
  }

  static async getOrganization(id: string): Promise<Organization> {
    const response = await apiClient.get<ApiResponse<Organization>>(`/api/organizations/${id}`);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to fetch organization');
    return response.data.data;
  }

  static async getOrganizationMembers(id: string): Promise<OrganizationMember[]> {
    const response = await apiClient.get<ApiResponse<OrganizationMember[]>>(`/api/organizations/${id}/members`);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to fetch members');
    return response.data.data;
  }

  static async createOrganization(data: Partial<Organization>): Promise<Organization> {
    const response = await apiClient.post<ApiResponse<Organization>>('/api/organizations', data);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to create organization');
    return response.data.data;
  }

  static async updateOrganization(id: string, data: Partial<Organization>): Promise<Organization> {
    const response = await apiClient.put<ApiResponse<Organization>>(`/api/organizations/${id}`, data);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to update organization');
    return response.data.data;
  }

  static async deleteOrganization(id: string): Promise<void> {
    const response = await apiClient.delete<ApiResponse<void>>(`/api/organizations/${id}`);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to delete organization');
  }

  static async addOrganizationMember(orgId: string, userId: string, role: string): Promise<OrganizationMember> {
    const response = await apiClient.post<ApiResponse<OrganizationMember>>(
      `/api/organizations/${orgId}/members`,
      { user_id: userId, role }
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to add member');
    return response.data.data;
  }

  static async removeOrganizationMember(orgId: string, userId: string): Promise<void> {
    const response = await apiClient.delete<ApiResponse<void>>(`/api/organizations/${orgId}/members/${userId}`);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to remove member');
  }

  // Sites
  static async listSites(params?: { page?: number; size?: number; organization_id?: string }): Promise<PaginatedData<Site>> {
    // Filter out undefined/null values before creating query string
    const cleanParams: Record<string, string> = {};
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value != null && String(value) !== '') {
          cleanParams[key] = String(value);
        }
      });
    }
    const queryString = new URLSearchParams(cleanParams).toString();
    const response = await apiClient.get<PaginatedData<Site>>(`/api/sites?${queryString}`);
    return response.data;
  }

  static async getSite(id: string): Promise<Site> {
    const response = await apiClient.get<ApiResponse<Site>>(`/api/sites/${id}`);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to fetch site');
    return response.data.data;
  }

  static async getSiteDevices(id: string): Promise<Device[]> {
    const response = await apiClient.get<ApiResponse<Device[]>>(`/api/sites/${id}/devices`);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to fetch site devices');
    return response.data.data;
  }

  static async createSite(data: Partial<Site>): Promise<Site> {
    const response = await apiClient.post<ApiResponse<Site>>('/api/sites', data);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to create site');
    return response.data.data;
  }

  static async updateSite(id: string, data: Partial<Site>): Promise<Site> {
    const response = await apiClient.put<ApiResponse<Site>>(`/api/sites/${id}`, data);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to update site');
    return response.data.data;
  }

  static async deleteSite(id: string): Promise<void> {
    const response = await apiClient.delete<ApiResponse<void>>(`/api/sites/${id}`);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to delete site');
  }

  // Devices
  static async listDevices(params?: {
    page?: number;
    size?: number;
    organization_id?: string;
    site_id?: string;
    status?: string;
  }): Promise<PaginatedData<Device>> {
    const cleanParams: Record<string, string> = {};
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value != null && String(value) !== '') {
          cleanParams[key] = String(value);
        }
      });
    }
    const queryString = new URLSearchParams(cleanParams).toString();
    const response = await apiClient.get<PaginatedData<Device>>(`/api/fleet/devices?${queryString}`);
    return response.data;
  }

  static async getDevice(id: string): Promise<Device> {
    const response = await apiClient.get<ApiResponse<Device>>(`/api/fleet/devices/${id}`);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to fetch device');
    return response.data.data;
  }

  static async getDeviceHeartbeats(id: string, days: number = 7): Promise<DeviceHeartbeat[]> {
    const response = await apiClient.get<ApiResponse<DeviceHeartbeat[]>>(`/api/fleet/devices/${id}/heartbeats?days=${days}`);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to fetch heartbeats');
    return response.data.data;
  }

  static async getDeviceEvents(id: string): Promise<DeviceEvent[]> {
    const response = await apiClient.get<ApiResponse<DeviceEvent[]>>(`/api/fleet/devices/${id}/events`);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to fetch events');
    return response.data.data;
  }

  static async createDevice(data: Partial<Device>): Promise<Device> {
    const response = await apiClient.post<ApiResponse<Device>>('/api/fleet/devices', data);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to create device');
    return response.data.data;
  }

  static async updateDevice(id: string, data: Partial<Device>): Promise<Device> {
    const response = await apiClient.put<ApiResponse<Device>>(`/api/fleet/devices/${id}`, data);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to update device');
    return response.data.data;
  }

  static async deleteDevice(id: string): Promise<void> {
    const response = await apiClient.delete<ApiResponse<void>>(`/api/fleet/devices/${id}`);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to delete device');
  }

  static async regenerateDeviceToken(id: string): Promise<{ provisioning_token: string }> {
    const response = await apiClient.post<ApiResponse<{ provisioning_token: string }>>(`/api/fleet/devices/${id}/regenerate-token`);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to regenerate token');
    return response.data.data;
  }

  // Alert Configs
  static async listAlertConfigs(organizationId?: string): Promise<AlertConfig[]> {
    const url = organizationId
      ? `/api/alerts/configs?organization_id=${organizationId}`
      : '/api/alerts/configs';
    const response = await apiClient.get<ApiResponse<AlertConfig[]>>(url);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to fetch alert configs');
    return response.data.data;
  }

  static async getAlertConfig(id: string): Promise<AlertConfig> {
    const response = await apiClient.get<ApiResponse<AlertConfig>>(`/api/alerts/configs/${id}`);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to fetch alert config');
    return response.data.data;
  }

  static async getAlertHistory(configId?: string): Promise<AlertHistory[]> {
    const url = configId
      ? `/api/alerts/history?config_id=${configId}`
      : '/api/alerts/history';
    const response = await apiClient.get<ApiResponse<AlertHistory[]>>(url);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to fetch alert history');
    return response.data.data;
  }

  static async createAlertConfig(data: Partial<AlertConfig>): Promise<AlertConfig> {
    const response = await apiClient.post<ApiResponse<AlertConfig>>('/api/alerts/configs', data);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to create alert config');
    return response.data.data;
  }

  static async updateAlertConfig(id: string, data: Partial<AlertConfig>): Promise<AlertConfig> {
    const response = await apiClient.put<ApiResponse<AlertConfig>>(`/api/alerts/configs/${id}`, data);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to update alert config');
    return response.data.data;
  }

  static async deleteAlertConfig(id: string): Promise<void> {
    const response = await apiClient.delete<ApiResponse<void>>(`/api/alerts/configs/${id}`);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to delete alert config');
  }

  static async testAlertWebhook(id: string): Promise<{ status: string; response_code?: number }> {
    const response = await apiClient.post<ApiResponse<{ status: string; response_code?: number }>>(`/api/alerts/test/${id}`);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to test webhook');
    return response.data.data;
  }

  // ---- Player Health (PWA player telemetry read side) ----

  static async listPlayerHealth(params?: {
    organization_id?: string;
    site_id?: string;
    zone_id?: string;
    status?: string;
    limit?: number;
    include_inactive?: boolean;
  }): Promise<PlayerHealthListResponse> {
    const cleanParams: Record<string, string> = {};
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value != null && String(value) !== '') {
          cleanParams[key] = String(value);
        }
      });
    }
    const queryString = new URLSearchParams(cleanParams).toString();
    const response = await apiClient.get<PlayerHealthListResponse>(`/api/fleet/player-health?${queryString}`);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to fetch player health');
    return response.data;
  }

  static async removePlayerDevice(deviceId: string): Promise<void> {
    const response = await apiClient.delete<ApiResponse<void>>(`/api/fleet/player-health/${deviceId}`);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to remove player device');
  }

  static async getPlayerHealthDevice(deviceId: string): Promise<PlayerHealthDevice> {
    const response = await apiClient.get<ApiResponse<PlayerHealthDevice>>(`/api/fleet/player-health/${deviceId}`);
    if (!response.data.success) throw new Error(response.data.error || 'Failed to fetch player device');
    return response.data.data;
  }

  static async listPlayerEvents(
    deviceId: string,
    params?: { type?: string; limit?: number; since?: string; before_id?: number }
  ): Promise<PlayerEventsResponse> {
    const cleanParams: Record<string, string> = {};
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value != null && String(value) !== '') {
          cleanParams[key] = String(value);
        }
      });
    }
    const queryString = new URLSearchParams(cleanParams).toString();
    const response = await apiClient.get<PlayerEventsResponse>(
      `/api/fleet/player-health/${deviceId}/events?${queryString}`
    );
    if (!response.data.success) throw new Error(response.data.error || 'Failed to fetch player events');
    return response.data;
  }
}
